import React, { useState, useEffect, useCallback } from 'react';
import { Gift, Send, MessageSquare, Loader2, PartyPopper } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Skeleton } from '../../../components/Skeleton';
import { TextArea } from '../../../components/TextArea';
import { Aniversariante, AniversarioMensagem } from '../../../types/aniversariantes';
import {
  listAniversariantesMes,
  enviarMensagemAniversario,
  listarMensagensAniversario,
  listarDestinatariosComMensagens,
} from '../../../services/aniversariantesService';
import { usePermissions, PERMISSOES } from '../../../hooks/usePermissions';
import { useToast } from '../../../hooks/use-toast';
import { useAuth } from '../../../contexts/AuthContext';

const NOMES_MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function getAge(dataNascimento: string): number {
  const hoje = new Date();
  const nascimento = new Date(dataNascimento + 'T00:00:00');
  let idade = hoje.getFullYear() - nascimento.getFullYear();
  const m = hoje.getMonth() - nascimento.getMonth();
  if (m < 0 || (m === 0 && hoje.getDate() < nascimento.getDate())) {
    idade--;
  }
  return idade;
}

function getDiaAniversario(dataNascimento: string): number {
  return parseInt(dataNascimento.split('-')[2], 10);
}

function isBirthdayToday(dataNascimento: string): boolean {
  const hoje = new Date();
  const [, mes, dia] = dataNascimento.split('-').map(Number);
  return hoje.getMonth() + 1 === mes && hoje.getDate() === dia;
}

interface MensagensDialogProps {
  aniversariante: Aniversariante;
  onClose: () => void;
}

function MensagensDialog({ aniversariante, onClose }: MensagensDialogProps) {
  const [mensagens, setMensagens] = useState<AniversarioMensagem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const load = async () => {
      try {
        const data = await listarMensagensAniversario(aniversariante.id);
        setMensagens(data);
      } catch {
        toast({ title: 'Erro', description: 'Falha ao carregar mensagens', variant: 'destructive' });
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [aniversariante.id, toast]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Mensagens para {aniversariante.nick}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : mensagens.length === 0 ? (
          <p className="text-center text-muted-foreground py-6 text-sm">
            Nenhuma mensagem recebida ainda.
          </p>
        ) : (
          <div className="space-y-3">
            {mensagens.map((msg) => (
              <div key={msg.id} className="rounded-lg border p-3 bg-muted/30">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm">{msg.remetente?.nick ?? '—'}</span>
                  <span className="text-xs text-muted-foreground">{msg.ano_aniversario}</span>
                </div>
                {msg.remetente?.patente && (
                  <p className="text-xs text-muted-foreground mb-1">{msg.remetente.patente.patente}</p>
                )}
                <p className="text-sm">{msg.mensagem}</p>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface EnviarMensagemDialogProps {
  aniversariante: Aniversariante;
  onClose: () => void;
  onSuccess: () => void;
}

function EnviarMensagemDialog({ aniversariante, onClose, onSuccess }: EnviarMensagemDialogProps) {
  const [mensagem, setMensagem] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mensagem.trim()) return;

    setIsSubmitting(true);
    try {
      await enviarMensagemAniversario(aniversariante.id, mensagem.trim());
      toast({ title: 'Sucesso!', description: `Mensagem enviada para ${aniversariante.nick}!` });
      onSuccess();
    } catch (error: any) {
      const msg = error?.response?.data?.message ?? 'Erro ao enviar mensagem';
      toast({ title: 'Erro', description: msg, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-pink-500" />
            Felicitar {aniversariante.nick}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground mb-3">
              Escreva uma mensagem de aniversário para{' '}
              <strong>{aniversariante.nick}</strong> ({aniversariante.patente.patente}).
            </p>
            <TextArea
              title="Mensagem"
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
              placeholder="Escreva sua mensagem de felicitações..."
              rows={4}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground text-right mt-1">{mensagem.length}/500</p>
          </div>

          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!mensagem.trim() || isSubmitting}>
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Enviar
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface AniversarianteCardProps {
  aniversariante: Aniversariante;
  jaFelicitou: boolean;
  onFelicitar: (a: Aniversariante) => void;
  canVerMensagens: boolean;
  onVerMensagens: (a: Aniversariante) => void;
}

function AniversarianteCard({
  aniversariante,
  jaFelicitou,
  onFelicitar,
  canVerMensagens,
  onVerMensagens,
}: AniversarianteCardProps) {
  const isHoje = isBirthdayToday(aniversariante.data_nascimento);
  const dia = getDiaAniversario(aniversariante.data_nascimento);
  const idade = getAge(aniversariante.data_nascimento) + 1; // completa no aniversário

  return (
    <Card className={`transition-all ${isHoje ? 'border-pink-400 dark:border-pink-500 shadow-sm' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-shrink-0">
            <img
              src={`https://www.habbo.com.br/habbo-imaging/avatarimage?img_format=png&user=${aniversariante.nick}&direction=3&head_direction=2&size=m&headonly=1`}
              alt={aniversariante.nick}
              className="w-12 h-12 rounded-full object-cover bg-muted"
              loading="lazy"
            />
            {isHoje && (
              <span className="absolute -top-1 -right-1 text-base" title="Aniversário hoje!">🎂</span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="font-semibold text-sm truncate">{aniversariante.nick}</p>
              {isHoje && (
                <PartyPopper className="w-3.5 h-3.5 text-pink-500 flex-shrink-0" />
              )}
            </div>
            <p className="text-xs text-muted-foreground truncate">{aniversariante.patente.patente}</p>
            <p className="text-xs text-muted-foreground">
              Dia {dia} · {idade} anos
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            {canVerMensagens && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                onClick={() => onVerMensagens(aniversariante)}
                title="Ver mensagens"
              >
                <MessageSquare className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button
              size="sm"
              variant={jaFelicitou ? 'outline' : 'default'}
              className={`h-7 px-2 text-xs ${jaFelicitou ? 'opacity-60' : ''}`}
              onClick={() => !jaFelicitou && onFelicitar(aniversariante)}
              disabled={jaFelicitou}
              title={jaFelicitou ? 'Você já felicitou este militar' : 'Enviar felicitações'}
            >
              <Gift className="w-3.5 h-3.5 mr-1" />
              {jaFelicitou ? 'Enviado' : 'Felicitar'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function Aniversariantes() {
  const hoje = new Date();
  const anoAtual = hoje.getFullYear();

  const [mesSelecionado, setMesSelecionado] = useState(hoje.getMonth()); // 0-indexed
  const [aniversariantes, setAniversariantes] = useState<Aniversariante[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [destinatariosMap, setDestinatariosMap] = useState<Map<string, number>>(new Map());
  const [isMensagensLoading, setIsMensagensLoading] = useState(false);
  const [felicitados, setFelicitados] = useState<Set<string>>(new Set());
  const [selectedAniversariante, setSelectedAniversariante] = useState<Aniversariante | null>(null);
  const [dialogMode, setDialogMode] = useState<'enviar' | 'mensagens' | null>(null);

  const { hasPermission } = usePermissions();
  const { militar } = useAuth();
  const { toast } = useToast();

  const canVerMensagens = hasPermission(PERMISSOES.ANIVERSARIANTES_VER_MENSAGENS);

  const loadAniversariantes = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await listAniversariantesMes(mesSelecionado + 1);
      setAniversariantes(data);
    } catch {
      toast({ title: 'Erro', description: 'Falha ao carregar aniversariantes', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [toast, mesSelecionado]);

  const loadDestinatariosComMensagens = useCallback(async () => {
    if (!canVerMensagens) return;
    setIsMensagensLoading(true);
    try {
      const data = await listarDestinatariosComMensagens();
      const map = new Map<string, number>();
      data.forEach((d) => map.set(d.id_militar_destinatario, d.total));
      setDestinatariosMap(map);
    } catch {
      toast({ title: 'Erro', description: 'Falha ao carregar dados de mensagens', variant: 'destructive' });
    } finally {
      setIsMensagensLoading(false);
    }
  }, [canVerMensagens, toast]);

  useEffect(() => {
    loadAniversariantes();
  }, [loadAniversariantes]);

  useEffect(() => {
    loadDestinatariosComMensagens();
  }, [loadDestinatariosComMensagens]);

  // Carregar quais já foram felicitados (persistido no localStorage por ano)
  useEffect(() => {
    if (!militar?.id) return;
    const key = `aniversario_felicitados_${anoAtual}_${militar.id}`;
    const salvo = localStorage.getItem(key);
    if (salvo) {
      try {
        setFelicitados(new Set(JSON.parse(salvo)));
      } catch {
        // ignora
      }
    }
  }, [militar?.id, anoAtual]);

  const marcarFelicitado = (id: string) => {
    setFelicitados((prev) => {
      const novo = new Set(prev);
      novo.add(id);
      if (militar?.id) {
        const key = `aniversario_felicitados_${anoAtual}_${militar.id}`;
        localStorage.setItem(key, JSON.stringify(Array.from(novo)));
      }
      return novo;
    });
  };

  const openEnviar = (a: Aniversariante) => {
    setSelectedAniversariante(a);
    setDialogMode('enviar');
  };

  const openMensagens = (a: Aniversariante) => {
    setSelectedAniversariante(a);
    setDialogMode('mensagens');
  };

  const closeDialog = () => {
    setSelectedAniversariante(null);
    setDialogMode(null);
  };

  const handleMensagemSucesso = () => {
    if (selectedAniversariante) {
      marcarFelicitado(selectedAniversariante.id);
    }
    closeDialog();
  };

  // Separar aniversariantes de hoje e do restante do mês (só aplica no mês atual)
  const isCurrentMonth = mesSelecionado === hoje.getMonth();
  const aniversariantesHoje = isCurrentMonth
    ? aniversariantes.filter((a) => isBirthdayToday(a.data_nascimento))
    : [];
  const aniversariantesRestante = isCurrentMonth
    ? aniversariantes.filter((a) => !isBirthdayToday(a.data_nascimento))
    : aniversariantes;

  const renderGrid = (lista: Aniversariante[]) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
      {lista.map((a) => (
        <AniversarianteCard
          key={a.id}
          aniversariante={a}
          jaFelicitou={felicitados.has(a.id)}
          onFelicitar={openEnviar}
          canVerMensagens={canVerMensagens}
          onVerMensagens={openMensagens}
        />
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-pink-100 dark:bg-pink-950 flex items-center justify-center flex-shrink-0">
            <Gift className="w-5 h-5 text-pink-600 dark:text-pink-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Aniversariantes</h1>
            <p className="text-muted-foreground text-sm">
              {NOMES_MESES[mesSelecionado]} de {anoAtual}
            </p>
          </div>
        </div>
        <select
          value={mesSelecionado}
          onChange={(e) => setMesSelecionado(Number(e.target.value))}
          className="border border-border rounded-md bg-background text-foreground text-sm px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-ring cursor-pointer"
        >
          {NOMES_MESES.map((nome, i) => (
            <option key={i} value={i}>{nome}</option>
          ))}
        </select>
      </div>

      <Tabs defaultValue="aniversariantes">
        <TabsList>
          <TabsTrigger value="aniversariantes" className="gap-2">
            <Gift className="w-4 h-4" />
            Aniversariantes
          </TabsTrigger>
          {canVerMensagens && (
            <TabsTrigger value="mensagens" className="gap-2">
              <MessageSquare className="w-4 h-4" />
              Mensagens
            </TabsTrigger>
          )}
        </TabsList>

        {/* Tab: Lista de Aniversariantes */}
        <TabsContent value="aniversariantes" className="mt-4 space-y-6">
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : aniversariantes.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Gift className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">
                  Nenhum aniversariante em {NOMES_MESES[mesSelecionado]}.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {aniversariantesHoje.length > 0 && (
                <div className="space-y-3">
                  <h2 className="text-sm font-semibold text-pink-600 dark:text-pink-400 flex items-center gap-1.5">
                    <PartyPopper className="w-4 h-4" />
                    Aniversário Hoje!
                  </h2>
                  {renderGrid(aniversariantesHoje)}
                </div>
              )}

              {aniversariantesRestante.length > 0 && (
                <div className="space-y-3">
                  {aniversariantesHoje.length > 0 && (
                    <h2 className="text-sm font-semibold text-muted-foreground">
                      Outros aniversariantes do mês
                    </h2>
                  )}
                  {renderGrid(aniversariantesRestante)}
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* Tab: Mensagens (restrita) */}
        {canVerMensagens && (
          <TabsContent value="mensagens" className="mt-4">
            {isLoading || isMensagensLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="h-24 w-full" />
                ))}
              </div>
            ) : (
              (() => {
                const comMensagens = aniversariantes.filter((a) => destinatariosMap.has(a.id));
                return comMensagens.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                      <p className="text-muted-foreground">
                        Nenhum aniversariante recebeu mensagens este mês.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                    {comMensagens.map((a) => {
                      const total = destinatariosMap.get(a.id) ?? 0;
                      return (
                        <Card
                          key={a.id}
                          className="cursor-pointer hover:border-primary transition-colors"
                          onClick={() => openMensagens(a)}
                        >
                          <CardContent className="p-4 flex items-center gap-3">
                            <img
                              src={`https://www.habbo.com.br/habbo-imaging/avatarimage?img_format=png&user=${a.nick}&direction=3&head_direction=2&size=m&headonly=1`}
                              alt={a.nick}
                              className="w-12 h-12 rounded-full object-cover bg-muted flex-shrink-0"
                              loading="lazy"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm truncate">{a.nick}</p>
                              <p className="text-xs text-muted-foreground truncate">{a.patente.patente}</p>
                              <p className="text-xs text-muted-foreground">
                                Dia {getDiaAniversario(a.data_nascimento)}
                              </p>
                            </div>
                            <div className="flex flex-col items-center gap-1 flex-shrink-0">
                              <MessageSquare className="w-4 h-4 text-muted-foreground" />
                              <span className="text-xs font-semibold text-primary">{total}</span>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                );
              })()
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Dialogs */}
      {selectedAniversariante && dialogMode === 'enviar' && (
        <EnviarMensagemDialog
          aniversariante={selectedAniversariante}
          onClose={closeDialog}
          onSuccess={handleMensagemSucesso}
        />
      )}

      {selectedAniversariante && dialogMode === 'mensagens' && (
        <MensagensDialog
          aniversariante={selectedAniversariante}
          onClose={closeDialog}
        />
      )}
    </div>
  );
}
